<template>
	<div class="flex flex-row items-center justify-between gap-x-4 p-1 sm:justify-end">
		<div class="flex items-center">
			<span class="mr-2 text-xs text-gray-500 dark:text-gray-400">Showing</span>
			<div class="flex items-center gap-0.5">
				<button
					v-for="size in pageSizes"
					:key="size"
					class="rounded px-1.5 py-0.5 text-xs font-medium transition-colors duration-150 ease-in-out"
					:class="[
						modelValue === size
							? 'bg-mainDark text-white'
							: 'text-main hover:bg-light-200 dark:text-white dark:hover:bg-offmaindark'
					]"
					@click="$emit('update:modelValue', size)"
				>
					{{ size }}
				</button>
			</div>
		</div>

		<div class="flex items-center gap-1">
			<button
				:disabled="currentPage <= 1"
				aria-label="First page"
				class="hidden h-7 w-7 items-center justify-center rounded bg-light-500 text-xs font-medium text-main transition-colors duration-150 ease-in-out hover:bg-light-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-offmain dark:text-white dark:hover:bg-mainLight sm:flex"
				@click="goToPage(1)"
			>
				<span class="fa-light fa-angles-left"></span>
			</button>

			<button
				:disabled="currentPage <= 1"
				aria-label="Previous page"
				class="hidden h-7 w-7 items-center justify-center rounded bg-light-500 text-xs font-medium text-main transition-colors duration-150 ease-in-out hover:bg-light-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-offmain dark:text-white dark:hover:bg-mainLight sm:flex"
				@click="goToPage(Math.max(1, currentPage - 1))"
			>
				<span class="fa-light fa-chevron-left"></span>
			</button>

			<div class="relative flex items-center gap-1">
				<button
					v-for="pageNum in visiblePageNumbers"
					:key="pageNum"
					class="hidden h-7 min-w-[28px] items-center justify-center rounded px-1 text-xs font-medium transition-colors duration-150 ease-in-out sm:flex"
					:class="[
						pageNum === currentPage
							? 'bg-mainDark text-white'
							: 'bg-light-500 text-main hover:bg-light-400 dark:bg-offmain dark:text-white dark:hover:bg-mainLight'
					]"
					@click="pageNum === currentPage ? togglePagePopover() : goToPage(pageNum)"
				>
					{{ pageNum }}
				</button>

				<div
					v-if="showPagePopover"
					class="absolute left-1/2 top-8 z-50 min-w-[120px] -translate-x-1/2 transform rounded-lg border border-gray-300 bg-white p-3 shadow-lg dark:border-gray-600 dark:bg-offmaindark"
					@click.stop
				>
					<div class="mb-2 text-xs text-gray-600 dark:text-gray-300">Go to page:</div>
					<input
						ref="pageInput"
						v-model="pageInputValue"
						type="number"
						:min="1"
						:max="totalPages.length"
						class="h-7 w-full rounded border border-gray-300 bg-white px-2 text-xs text-main focus:border-mainDark focus:ring-1 focus:ring-mainDark dark:border-gray-600 dark:bg-offmain dark:text-white"
						@keyup.enter="handlePageInput"
						@keyup.escape="closePagePopover"
					/>
					<div class="mt-2 flex gap-1">
						<button
							class="h-6 flex-1 rounded bg-mainDark text-xs text-white transition-colors hover:bg-main"
							@click="handlePageInput"
						>
							Go
						</button>
						<button
							class="h-6 flex-1 rounded bg-gray-300 text-xs text-gray-700 transition-colors hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
							@click="closePagePopover"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>

			<div class="flex items-center gap-1 sm:hidden">
				<button
					v-if="totalPages.length > 0"
					:disabled="currentPage <= 1"
					aria-label="First page"
					class="flex h-7 w-7 items-center justify-center rounded bg-light-500 text-xs font-medium text-main transition-colors duration-150 ease-in-out hover:bg-light-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-offmain dark:text-white dark:hover:bg-mainLight"
					@click="goToPage(1)"
				>
					<span class="fa-light fa-angles-left"></span>
				</button>

				<button
					v-if="totalPages.length > 0"
					:disabled="currentPage <= 1"
					aria-label="Previous page"
					class="flex h-7 w-7 items-center justify-center rounded bg-light-500 text-xs font-medium text-main transition-colors duration-150 ease-in-out hover:bg-light-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-offmain dark:text-white dark:hover:bg-mainLight"
					@click="goToPage(Math.max(1, currentPage - 1))"
				>
					<span class="fa-light fa-chevron-left"></span>
				</button>

				<div class="relative">
					<button
						v-if="totalPages.length > 0"
						class="h-7 min-w-[60px] rounded bg-mainDark px-2 text-xs font-medium text-white transition-colors hover:bg-main"
						@click="toggleMobilePagePopover"
					>
						{{ currentPage }} / {{ totalPages.length }}
					</button>

					<span
						v-if="totalPages.length === 0"
						class="flex h-7 items-center justify-center px-2 text-xs font-medium text-gray-400 dark:text-gray-500"
					>
						No pages
					</span>

					<div
						v-if="showMobilePagePopover && totalPages.length > 0"
						class="absolute left-1/2 top-8 z-50 min-w-[120px] -translate-x-1/2 transform rounded-lg border border-gray-300 bg-white p-3 shadow-lg dark:border-gray-600 dark:bg-offmaindark"
						@click.stop
					>
						<div class="mb-2 text-xs text-gray-600 dark:text-gray-300">Go to page:</div>
						<input
							ref="mobilePageInput"
							v-model="mobilePageInputValue"
							type="number"
							:min="1"
							:max="totalPages.length"
							class="h-7 w-full rounded border border-gray-300 bg-white px-2 text-xs text-main focus:border-mainDark focus:ring-1 focus:ring-mainDark dark:border-gray-600 dark:bg-offmain dark:text-white"
							@keyup.enter="handleMobilePageInput"
							@keyup.escape="closeMobilePagePopover"
						/>
						<div class="mt-2 flex gap-1">
							<button
								class="h-6 flex-1 rounded bg-mainDark text-xs text-white transition-colors hover:bg-main"
								@click="handleMobilePageInput"
							>
								Go
							</button>
							<button
								class="h-6 flex-1 rounded bg-gray-300 text-xs text-gray-700 transition-colors hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
								@click="closeMobilePagePopover"
							>
								Cancel
							</button>
						</div>
					</div>
				</div>

				<button
					v-if="totalPages.length > 0"
					:disabled="currentPage >= totalPages.length"
					aria-label="Next page"
					class="flex h-7 w-7 items-center justify-center rounded bg-light-500 text-xs font-medium text-main transition-colors duration-150 ease-in-out hover:bg-light-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-offmain dark:text-white dark:hover:bg-mainLight"
					@click="goToPage(Math.min(totalPages.length, currentPage + 1))"
				>
					<span class="fa-light fa-chevron-right"></span>
				</button>

				<button
					v-if="totalPages.length > 0"
					:disabled="currentPage >= totalPages.length"
					aria-label="Last page"
					class="flex h-7 w-7 items-center justify-center rounded bg-light-500 text-xs font-medium text-main transition-colors duration-150 ease-in-out hover:bg-light-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-offmain dark:text-white dark:hover:bg-mainLight"
					@click="goToPage(totalPages.length)"
				>
					<span class="fa-light fa-angles-right"></span>
				</button>
			</div>

			<button
				:disabled="currentPage >= totalPages.length || totalPages.length === 0"
				aria-label="Next page"
				class="hidden h-7 w-7 items-center justify-center rounded bg-light-500 text-xs font-medium text-main transition-colors duration-150 ease-in-out hover:bg-light-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-offmain dark:text-white dark:hover:bg-mainLight sm:flex"
				@click="goToPage(Math.min(totalPages.length, currentPage + 1))"
			>
				<span class="fa-light fa-chevron-right"></span>
			</button>

			<button
				:disabled="currentPage >= totalPages.length"
				aria-label="Last page"
				class="hidden h-7 w-7 items-center justify-center rounded bg-light-500 text-xs font-medium text-main transition-colors duration-150 ease-in-out hover:bg-light-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-offmain dark:text-white dark:hover:bg-mainLight sm:flex"
				@click="goToPage(totalPages.length)"
			>
				<span class="fa-light fa-angles-right"></span>
			</button>
		</div>
	</div>

	<div v-if="showPagePopover || showMobilePagePopover" class="fixed inset-0 z-40" @click="closeAllPopovers"></div>
</template>

<script>
export default {
	props: {
		modelValue: Number,
		currentPage: {
			type: Number,
			default: 1
		},
		totalPages: {
			type: Array,
			default: () => []
		},
		pageSizes: {
			type: Array,
			default: () => [10, 25, 100]
		},
		visiblePageCount: {
			type: Number,
			default: 3
		}
	},
	emits: ['update:modelValue', 'page-change'],
	data() {
		return {
			showPagePopover: false,
			pageInputValue: '',
			showMobilePagePopover: false,
			mobilePageInputValue: ''
		}
	},
	computed: {
		visiblePageNumbers() {
			const totalPageCount = this.totalPages.length
			if (totalPageCount === 0) return []

			const pageCountToShow = this.visiblePageCount

			if (totalPageCount <= pageCountToShow + 2) {
				return Array.from({ length: totalPageCount }, (_, i) => i + 1)
			}

			const pages = []
			let startPage, endPage

			if (this.currentPage <= Math.ceil(pageCountToShow / 2)) {
				startPage = 1
				endPage = pageCountToShow
			} else if (this.currentPage + Math.floor(pageCountToShow / 2) >= totalPageCount) {
				startPage = totalPageCount - pageCountToShow + 1
				endPage = totalPageCount
			} else {
				startPage = this.currentPage - Math.floor(pageCountToShow / 2)
				endPage = this.currentPage + Math.ceil(pageCountToShow / 2) - 1
			}

			for (let i = startPage; i <= endPage; i++) {
				pages.push(i)
			}
			return pages
		}
	},
	methods: {
		goToPage(pageNum) {
			const newPage = parseInt(pageNum, 10)
			if (newPage >= 1 && newPage <= this.totalPages.length && newPage !== this.currentPage) {
				this.$emit('page-change', newPage)
			}
		},
		togglePagePopover() {
			this.showPagePopover = !this.showPagePopover
			if (this.showPagePopover) {
				this.pageInputValue = this.currentPage.toString()
				this.$nextTick(() => {
					if (this.$refs.pageInput) {
						this.$refs.pageInput.focus()
						this.$refs.pageInput.select()
					}
				})
			}
		},
		closePagePopover() {
			this.showPagePopover = false
			this.pageInputValue = ''
		},
		handlePageInput() {
			const inputPage = parseInt(this.pageInputValue, 10)

			if (isNaN(inputPage)) {
				return
			}

			let targetPage = inputPage

			if (targetPage < 1) {
				targetPage = 1
			} else if (targetPage > this.totalPages.length) {
				targetPage = this.totalPages.length
			}

			if (targetPage !== this.currentPage) {
				this.goToPage(targetPage)
			}

			this.closePagePopover()
		},
		toggleMobilePagePopover() {
			this.showMobilePagePopover = !this.showMobilePagePopover
			if (this.showMobilePagePopover) {
				this.mobilePageInputValue = this.currentPage.toString()
				this.$nextTick(() => {
					if (this.$refs.mobilePageInput) {
						this.$refs.mobilePageInput.focus()
						this.$refs.mobilePageInput.select()
					}
				})
			}
		},
		closeMobilePagePopover() {
			this.showMobilePagePopover = false
			this.mobilePageInputValue = ''
		},
		handleMobilePageInput() {
			const inputPage = parseInt(this.mobilePageInputValue, 10)

			if (isNaN(inputPage)) {
				return
			}

			let targetPage = inputPage

			if (targetPage < 1) {
				targetPage = 1
			} else if (targetPage > this.totalPages.length) {
				targetPage = this.totalPages.length
			}

			if (targetPage !== this.currentPage) {
				this.goToPage(targetPage)
			}

			this.closeMobilePagePopover()
		},
		closeAllPopovers() {
			this.closePagePopover()
			this.closeMobilePagePopover()
		}
	}
}
</script>
